import { useState, useEffect } from "react";
import {
  Card,
  IndexTable,
  BlockStack,
  IndexFilters,
  Page,
  IndexFiltersMode,
  Badge,
  Text,
  InlineStack,
  Box,
  Tooltip,
  Icon,
  IndexFiltersProps,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { AppPageWrapper } from "@shared/ui";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useCountries } from "@shared/hooks/common/useCountries";
import { TabsTableSkeleton } from "@shared/ui";
import { useEvents } from "@features/events/hooks/useEventsDashboard";
import AttendeesImportExportModal from "@features/events/components/AttendeesImportExportModal";
import { SearchableChoiceList } from "@shared/ui";
import { FilterComponentRenderer } from "@shared/ui";
import { Tag } from "@shared/ui";
import { NoteIcon } from "@medentic/polaris-icons";
import { getTone } from "@features/clinic-patients/clinic/utils";
import { CopyToClipboard } from "@shared/ui";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { EVENT_CURRENCY_DEFAULT } from "@features/events/constants";
import { Currency } from "@shared/ui";
import { 
  Attendee, 
  AttendeeTag, 
  AttendeeView, 
  EventListItem as Event
} from "@features/events/types";

export default function EventAttendees() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const serializedSearchParams = searchParams.toString();
  const { GET } = useApiRequest();
  const { categoryOptions, metadata } = useEventMetadata();

  const { data: tags = [], isLoading: isLoadingTags } = useQuery<AttendeeTag[]>(
    {
      queryKey: ["attendee-tags", hcc],
      queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/attendees/tags`),
      enabled: Boolean(hcc),
    }
  );

  const { data: events = [], isLoading: eventsLoading } = useEvents(
    hcc,
    undefined
  ) as {
    data: Event[];
    isLoading: boolean;
  };

  const { countries } = useCountries();

  const { data: attendeeTags = [] } = useQuery<AttendeeTag[]>({
    queryKey: ["attendee-tags", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/attendees/tags`),
    enabled: Boolean(hcc),
  });

  const getCountryName = (countryNameOrCode: string) => {
    const found = countries.find((c) => c.iso3 === countryNameOrCode);
    return found ? found.name : countryNameOrCode;
  };

  // State
  const [selectedView, setSelectedView] = useState(
    () => searchParams.get("view") || "all"
  );
  const [queryIds] = useState(() => searchParams.get("Ids") || "");
  const [queryValue, setQueryValue] = useState(
    () => searchParams.get("search") || ""
  );
  const [debouncedQuery, setDebouncedQuery] = useState(
    () => searchParams.get("search") || ""
  );
  const [selectedEvents, setSelectedEvents] = useState<string[]>(() => {
    const ev = searchParams.get("events");
    return ev ? ev.split(",") : [];
  });
  const [selectedTags, setSelectedTags] = useState<string[]>(() => {
    const tags = searchParams.get("tags");
    return tags ? tags.split(",") : [];
  });
  const [selectedTopics, setSelectedTopics] = useState<string[]>(() => {
    const topics = searchParams.get("topicsOfInterestIds");
    return topics ? topics.split(",") : [];
  });
  const [sortSelected, setSortSelected] = useState<string[]>(() => {
    const sortParam = searchParams.get("sort");
    if (sortParam) return [sortParam];
    
    // Check localStorage for saved sorting preference
    const savedSort = localStorage.getItem("events_attendees_sorting");
    return savedSort ? [savedSort] : ["updated desc"];
  });
  const [mode, setMode] = useState(IndexFiltersMode.Default);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const csvExampleUrl = `/api/healthcare-companies/${hcc}/e/attendees/import-csv`;

  // Sort options
  const sortOptions: IndexFiltersProps['sortOptions'] = [
    {label: t('attendees.sort_by.created'), value: 'created asc', directionLabel: t('common.oldestToNewest')},
    {label: t('attendees.sort_by.created'), value: 'created desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('attendees.sort_by.modified'), value: 'updated asc', directionLabel: t('common.oldestToNewest')},
    {label: t('attendees.sort_by.modified'), value: 'updated desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('attendees.sort_by.name'), value: 'name asc', directionLabel: t('common.AtoZ')},
    {label: t('attendees.sort_by.name'), value: 'name desc', directionLabel: t('common.ZtoA')},
    
    {label: t('attendees.sort_by.paid'), value: 'paid asc', directionLabel: t('common.LowestToHighest')},
    {label: t('attendees.sort_by.paid'), value: 'paid desc', directionLabel: t('common.HighestToLowest')},
    
    {label: t('attendees.sort_by.email'), value: 'email asc', directionLabel: t('common.AtoZ')},
    {label: t('attendees.sort_by.email'), value: 'email desc', directionLabel: t('common.ZtoA')},
    
    {label: t('attendees.sort_by.phone'), value: 'phone asc', directionLabel: t('common.AtoZ')},
    {label: t('attendees.sort_by.phone'), value: 'phone desc', directionLabel: t('common.ZtoA')},
    
    {label: t('attendees.sort_by.companyName'), value: 'companyName asc', directionLabel: t('common.AtoZ')},
    {label: t('attendees.sort_by.companyName'), value: 'companyName desc', directionLabel: t('common.ZtoA')},
  ];

  // Save sorting preference to localStorage
  useEffect(() => {
    if (sortSelected[0]) {
      localStorage.setItem("events_attendees_sorting", sortSelected[0]);
    }
  }, [sortSelected]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(queryValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [queryValue]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedView && selectedView !== "all")
      params.set("view", selectedView);
    if (debouncedQuery) params.set("search", debouncedQuery);
    if (selectedEvents.length) params.set("events", selectedEvents.join(","));
    if (selectedTags.length) params.set("tags", selectedTags.join(","));
    if (selectedTopics.length)
      params.set("topicsOfInterestIds", selectedTopics.join(","));
    if (queryIds) params.set("Ids", queryIds);
    if (sortSelected[0]) params.set("sort", sortSelected[0]);
    setSearchParams(params);
  }, [
    selectedView,
    debouncedQuery,
    selectedEvents,
    selectedTags,
    selectedTopics,
    sortSelected,
    setSearchParams,
    queryIds,
  ]);

  // Fetch views (tabs)
  const { data: views = [] } = useQuery<AttendeeView[]>({
    queryKey: ["attendee-views", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/attendees/views`),
    enabled: Boolean(hcc),
  });

  // Fetch attendees
  const {
    data: attendees = [],
    isLoading,
    isFetching,
  } = useQuery<Attendee[]>({
    queryKey: [
      "attendees",
      hcc,
      selectedView,
      debouncedQuery,
      selectedEvents.join(","),
      selectedTags.join(","),
      selectedTopics.join(","),
      sortSelected,
      queryIds,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedView && selectedView !== "all") {
        const selectedViewData = views.find((v) => String(v.id) === selectedView);
        if (selectedViewData?.filters) {
          // Add sort parameters
          params.append("sortField", selectedViewData.filters.sortFiled);
          params.append("sortDirection", selectedViewData.filters.sortDirection);

          // Add filter parameters
          Object.entries(selectedViewData.filters.filters).forEach(
            ([key, value]) => {
              if (value) params.append(key, value);
            }
          );
        }
      }
      if (debouncedQuery) params.append("query", debouncedQuery);
      if (selectedEvents.length)
        params.append("eventIds", selectedEvents.join(","));
      if (selectedTags.length) params.append("tagIds", selectedTags.join(","));
      if (selectedTopics.length)
        params.append("topicsOfInterestIds", selectedTopics.join(","));
      if (sortSelected[0]) params.append("sort", sortSelected[0]);
      if (queryIds) params.append("ids", queryIds);
      
      return GET(`/api/healthcare-companies/${hcc}/e/attendees?${params}`);
    },
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });

  // Table headings
  const headings: { title: string; alignment?: "start" | "end" }[] = [
    { title: t("attendees.name") },
    { title: t("attendees.phone") },
    { title: t("attendees.email") },
    { title: t("attendees.emailSubscription") },
    { title: t("attendees.location") },
    { title: t("attendees.eventsAttended"), alignment: "end" },
    { title: t("attendees.totalSpent"), alignment: "end" },
    { title: t("tags.tags") },
  ];

  // Table rows
  const rowMarkup = attendees.map((a: Attendee, idx: number) => (
    <IndexTable.Row
      id={a.id}
      key={a.id}
      position={idx}
      onClick={() => {
        navigate(
          `/e/${hcc}/attendees/${a.id}${
            serializedSearchParams ? `?${serializedSearchParams}` : ""
          }`
        );
      }}
    >
      <IndexTable.Cell>
        <InlineStack gap="200" wrap={false}>
          {[a.firstName, a.lastName].filter(Boolean).join(" ") || "-"}
          {a.privateNote && (
            <Box>
              <Tooltip content={a.privateNote}>
                <Icon source={NoteIcon} tone={getTone(a.privateNoteTone)} />
              </Tooltip>
            </Box>
          )}
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {a.phoneNumber && a.phoneCountryCode ? (
          <InlineStack gap="200" wrap={false}>
            {`${a.phoneCountryCode} ${a.phoneNumber}`}
            <CopyToClipboard text={`${a.phoneCountryCode} ${a.phoneNumber}`} />
          </InlineStack>
        ) : (
          "-"
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {a.email ? (
          <InlineStack gap="200" wrap={false}>
            {a.email}
            <CopyToClipboard text={a.email} />
          </InlineStack>
        ) : (
          ""
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={a.agreedMarketingEmail ? "success" : "critical"}>
          {a.agreedMarketingEmail
            ? t("attendees.subscribed")
            : t("attendees.notSubscribed")}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{getCountryName(a.country) || "-"}</IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" alignment="end">
          {a.totalVisitedEvents ?? 0}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" alignment="end">
          <Currency
            value={a.paymentsTotalSpent ?? 0}
            currency={EVENT_CURRENCY_DEFAULT}
          />
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {a.tagIds && a.tagIds.length > 0 ? (
          <InlineStack gap="100" wrap={false}>
            {a.tagIds.slice(0, 3).map((tagId) => (
              <Tag
                key={tagId}
                tagLabel={tags.find((t) => t.id === tagId)?.name || ""}
                tagId={tagId}
                colorName={tags.find((t) => t.id === tagId)?.color || ""}
              />
            ))}
            {a.tagIds.length > 3 && <Badge>{`+${a.tagIds.length - 3}`}</Badge>}
          </InlineStack>
        ) : (
          ""
        )}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  // Filters
  const filters = [
    {
      key: "events",
      label: t("attendees.events"),
      filter: (
        <SearchableChoiceList
          label={""}
          options={events.map((event: Event) => ({
            label: event.publicTitle,
            value: event.id,
          }))}
          value={selectedEvents.join(",")}
          onChange={(val) => setSelectedEvents(val.split(","))}
          disabled={false}
          helpText={""}
          error={""}
        />
      ),
      pinned: true,
    },
    {
      key: "tags",
      label: t("tags.tags"),
      filter: (
        <FilterComponentRenderer
          key="tags"
          id="combobox"
          label={t("tags.tags")}
          value={selectedTags}
          onChange={(val) => setSelectedTags(val)}
          disabled={false}
          helpText=""
          placeholder=""
          comboOptions={attendeeTags.map((tag) => ({
            label: tag.name,
            value: tag.id,
          }))}
        />
      ),
    },
    {
      key: "topicsOfInterestIds",
      label: t("attendeeDetails.topicsOfInterest.title"),
      filter: (
        <FilterComponentRenderer
          key="topics"
          id="combobox"
          label={t("attendeeDetails.topicsOfInterest.title")}
          value={selectedTopics}
          onChange={(val) => setSelectedTopics(val)}
          disabled={false}
          helpText=""
          placeholder=""
          comboOptions={categoryOptions}
        />
      ),
    },
  ];

  // Applied filters
  const appliedFilters = [];
  if (selectedEvents.length) {
    const selectedEventNames = selectedEvents
      .map((eventId) => {
        const event = events.find((e) => e.id === eventId);
        return event ? event.publicTitle : eventId;
      })
      .join(", ");

    appliedFilters.push({
      key: "events",
      label: `${t("attendees.events")}: ${selectedEventNames}`,
      onRemove: () => setSelectedEvents([]),
    });
  }

  if (selectedTags.length) {
    const selectedTagNames = selectedTags
      .map((tagId) => {
        const tag = attendeeTags.find((t) => t.id === tagId);
        return tag ? tag.name : tagId;
      })
      .join(", ");

    appliedFilters.push({
      key: "tags",
      label: `${t("tags.tags")}: ${selectedTagNames}`,
      onRemove: () => setSelectedTags([]),
    });
  }

  if (selectedTopics.length) {
    const selectedTopicNames = selectedTopics
      .map((topicId) => {
        const topic = (metadata?.categories || []).find(
          (c) => c.id === topicId
        );
        return topic ? topic.name : topicId;
      })
      .join(", ");

    appliedFilters.push({
      key: "topicsOfInterestIds",
      label: `${t(
        "attendeeDetails.topicsOfInterest.title"
      )}: ${selectedTopicNames}`,
      onRemove: () => setSelectedTopics([]),
    });
  }

  const isInitialLoading = isLoading && attendees.length === 0;

  const handleExport = async () => {
    try {
      setIsLoadingExport(true);
      // Build query parameters to include current filters
      const params = new URLSearchParams();
      if (selectedView && selectedView !== "all") {
        const selectedViewData = views.find(
          (v) => String(v.id) === selectedView
        );
        if (selectedViewData?.filters) {
          params.append("sortField", selectedViewData.filters.sortFiled);
          params.append(
            "sortDirection",
            selectedViewData.filters.sortDirection
          );
          Object.entries(selectedViewData.filters.filters).forEach(
            ([key, value]) => {
              if (value) params.append(key, value);
            }
          );
        }
      }
      if (debouncedQuery) params.append("query", debouncedQuery);
      if (selectedEvents.length)
        params.append("eventIds", selectedEvents.join(","));
      if (selectedTags.length) params.append("tagIds", selectedTags.join(","));
      if (sortSelected[0]) params.append("sort", sortSelected[0]);
      if (queryIds) params.append("ids", queryIds);

      const result = await GET<{ url: string }>(
        `/api/healthcare-companies/${hcc}/e/attendees/export?${params}`
      );

      if (result?.url) {
        // Use the pre-signed URL from the response
        const a = document.createElement("a");
        a.href = result.url;
        a.download = "attendees.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        console.error("Export failed: No URL returned");
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsLoadingExport(false);
    }
  };

  return (
    <AppPageWrapper>
      <Page
        fullWidth
        title={t("attendees.title")}
        primaryAction={{
          content: t("attendees.addAttendee"),
          onAction: () => navigate(`/e/${hcc}/attendees/new`),
          disabled: false,
        }}
        secondaryActions={[
          {
            content: t("attendees.export"),
            onAction: handleExport,
            loading: isLoadingExport,
          },
          {
            content: t("attendees.import"),
            onAction: () => setImportModalOpen(true),
          },
        ]}
      >
        {isInitialLoading || eventsLoading || isLoadingTags ? (
          <TabsTableSkeleton />
        ) : (
          <Card padding="0">
            <BlockStack gap="0">
              <IndexFilters
                tabs={(views || []).map((v) => ({
                  content: v.name,
                  id: String(v.id),
                }))}
                selected={Math.max(
                  0,
                  (views || []).findIndex((v) => String(v?.id) === selectedView)
                )}
                onSelect={(idx) =>
                  setSelectedView(String(views[idx]?.id || "all"))
                }
                queryValue={queryValue}
                queryPlaceholder={t("attendees.searchPlaceholder")}
                onQueryChange={setQueryValue}
                onQueryClear={() => setQueryValue("")}
                canCreateNewView={false}
                filters={filters}
                appliedFilters={appliedFilters}
                onClearAll={() => {
                  setSelectedView("all");
                  setSelectedEvents([]);
                  setSelectedTags([]);
                  setSelectedTopics([]);
                  setQueryValue("");
                  setSortSelected(["updated desc"]);
                }}
                sortOptions={sortOptions}
                sortSelected={sortSelected}
                onSort={setSortSelected}
                mode={mode}
                setMode={setMode}
                loading={isFetching && attendees.length > 0}
              />
              <IndexTable
                resourceName={{
                  singular: t("attendees.attendee"),
                  plural: t("attendees.attendees"),
                }}
                itemCount={attendees.length}
                selectable={false}
                headings={
                  headings as [{ title: string }, ...{ title: string }[]]
                }
              >
                {rowMarkup}
              </IndexTable>
            </BlockStack>
          </Card>
        )}
        <AttendeesImportExportModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          csvExampleUrl={csvExampleUrl}
        />
      </Page>
    </AppPageWrapper>
  );
}
